import { type ImpactKind } from "./combatSoundsLogic";

/*
    Damage that stays (0.98.7): the parts without Pixi (tests/src/update0987.test.ts). See managers/damageMarks.ts.
*/

/** at most this many of each (the oldest go first): the holes counted whether their thing is on screen or not */
export const MOST = Object.freeze({ holes: 900, holesOnOne: 26, scorches: 320, debris: 1400 });

export type HoleKind = "stone" | "wood" | "metal" | "glass" | "card" | "dirt";

/** which hole a bullet leaves in what it struck (none in foliage, a tree's bark or something soft) */
export function holeKind(kind: ImpactKind): HoleKind | undefined {
    switch (kind) {
        case "stone":
        case "ceramic": return "stone";
        case "wood": return "wood";
        case "metal":
        case "metal_heavy": return "metal";
        case "glass": return "glass";
        case "cardboard": return "card";
        case "dirt": return "dirt";
        default: return undefined;
    }
}

/**
 * the server's blast marks that are burns (common/src/definitions/decals.ts, drawn by tools/props/props.py in 1.01's
 * look, which they keep): kept as they are, for everyone who comes by while the server keeps them. A smoke grenade's
 * mark, a seed's and every other decal aren't burns
 */
export const BURN_DECALS: ReadonlySet<string> = new Set(["explosion_decal", "frag_explosion_decal"]);

/** a blast with no mark of its own burns the ground from this reach (game units): a barrel's, a stove's, a silo's */
export const MARK_LEAST_REACH = 10;
/** a frag grenade's reach, whose mark is its picture's own size (about 5 units across) */
export const MARK_REACH = 25;

/**
 * what a blast leaves (0.98.7), or none: one that burns (its own mark one of `BURN_DECALS`, or none of its own and a
 * reach of `MARK_LEAST_REACH` or more) leaves its debris about it, thrown over `debris` units, and one with no mark of
 * its own gets the blast mark's picture (`picture`, its frame; `scale` of its size: a stove's as a grenade's, a silo's
 * twice and more) from the page, kept for the match. Never a smoke grenade's, a seed's, a match's, a gas grenade's
 * pop or a molotov's flame
 */
export function blastMarks(decal: string | undefined, reach: number): { readonly debris: number, readonly picture?: string, readonly scale?: number } | undefined {
    if (decal !== undefined ? !BURN_DECALS.has(decal) : reach < MARK_LEAST_REACH) return undefined;
    const scale = Math.min(2.4, Math.max(0.6, reach / MARK_REACH));
    const debris = 2.6 * scale;
    if (decal !== undefined) return { debris };
    return { debris, picture: reach >= 20 ? "frag_explosion_decal" : "explosion_decal", scale };
}

/**
 * whether the holes kept for an obstacle that went off screen are its own when an obstacle of that number comes back:
 * the same kind on the same floor where it was (a number freed by a removed thing can be given to a new one)
 */
export function sameObstacle(
    kept: { readonly idString: string, readonly layer: number, readonly x: number, readonly y: number },
    now: { readonly idString: string, readonly layer: number, readonly x: number, readonly y: number }
): boolean {
    return kept.idString === now.idString && kept.layer === now.layer && Math.hypot(kept.x - now.x, kept.y - now.y) < 1;
}
